/**
 * What the steps before a Rule 506(b) signature are called on the screen, and which of them a
 * person still owes (S-30). Pure functions: the screen and its test share them.
 *
 * The codes are the server's (`reg_d_package.assess`); the server decides what is missing, this
 * only words it.
 */
import type { RegDDocument, RegDStatus } from '../../services/regD.service';

export const STEP_LABEL: Record<string, string> = {
  consent: 'Agree to receive these documents electronically',
  package_unavailable: 'The documents of this offering are not available yet',
  disclosure_unavailable: 'A required disclosure is not available yet',
  acknowledgement: 'Open every document and confirm you received them',
  waiting_period: 'Allow time to read the documents before you sign',
  sophistication: 'Answer the questions about your experience',
  sophistication_review: 'Wait for our team to review your answers',
  sophistication_refused: 'Your answers did not show the experience this offering requires',
};

/** The steps a person with this answer still owes: none-of-these is the non-accredited list. */
export function stepsOwed(status: RegDStatus, category: string | null): string[] {
  return category === 'not_accredited' ? status.non_accredited_missing : status.accredited_missing;
}

/** The wording of each owed step, in the order the person does them. */
export function stepLabels(owed: readonly string[]): string[] {
  return owed.map((code) => STEP_LABEL[code] ?? 'Finish the remaining steps');
}

const DOC_TITLE: Record<string, string> = {
  reg_d_package: 'Information package',
  bad_actor_disclosure: 'Disclosure of past events',
  ppm: 'Offering memorandum',
  term_sheet: 'Term sheet',
  supplement: 'Supplement',
};

/** "Exhibit: operating agreement" for the slot `operating_agreement`; the rest by their kind. */
export function documentTitle(doc: Pick<RegDDocument, 'doc_type' | 'exhibit_slot'>): string {
  if (doc.doc_type === 'exhibit') {
    const slot = (doc.exhibit_slot ?? '').replace(/_/g, ' ');
    return slot ? `Exhibit: ${slot}` : 'Exhibit';
  }
  return DOC_TITLE[doc.doc_type] ?? 'Document';
}

/** Whether every document has been opened (the acknowledgement needs it). */
export function allOpened(documents: readonly RegDDocument[]): boolean {
  return documents.length > 0 && documents.every((d) => d.delivered);
}

/** Whether the person acknowledged the documents now published. */
export function allAcknowledged(documents: readonly RegDDocument[]): boolean {
  return documents.length > 0 && documents.every((d) => d.acknowledged);
}

/** Whether the steps section has anything to say to this person at all. */
export function hasPackage(status: RegDStatus): boolean {
  return status.documents.length > 0;
}
